import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ChevronLeft, MoreVertical, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import {
  EntityAvatar,
  FeatureDetailsFooter,
  FeatureTabRef,
  MobileTabs,
  ResponsiveTabs,
  StatusBadge,
  useFeatureDetailsFormManager,
} from '@/modules/common';
import { PatchEntityDetailsResultModal } from '@/modules/common/components/templates/feature-details/components/patch-entity-details-result.modal';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';

import { ErrorState } from '../../organisms/error-state';
import { FullPageLoader } from '../../organisms/file/full-page-loader';
import {
  ActionButtonConfig,
  BaseTabFormData,
  FeatureDetailsProps,
  FeatureEntity,
  FeatureMode,
} from './types/types';

/**
 * Generic feature details template that provides common functionality for
 * detail pages with tabs, forms, and multi-mode operations (Create/Edit/Read).
 *
 * This template handles:
 * - Multi-mode operation with state management
 * - Tab-based navigation with form state
 * - Header with navigation, avatar, and actions
 * - Footer with mode-specific controls
 * - Loading and error states
 * - Responsive design
 * - Tab form management with draft persistence and validation
 * - Form submission coordination across tabs
 */
export function FeatureDetailsTemplate<
  T extends FeatureEntity = FeatureEntity,
  TFormData extends BaseTabFormData = BaseTabFormData,
>({
  entityData,
  mode,
  currentTab,
  isLoading,
  isLoadingEntity,
  error,
  tabs,
  getDraftData,
  saveDraftData,
  clearDraft,
  getFinalSubmissionData,
  hasDraftData,
  calculateHasAnyData,
  handleSave,
  // Actions
  onBack,
  onFormStateChange,
  onTabChange: parentOnTabChange,
  onRefetch,
  onFormStateChangeHandlerReady,
  onTabChangeHandlerReady,
  onAllowNavigationReady,
  // Unsaved changes confirmation
  enableUnsavedChangesConfirmation = false,
  // Customization props
  pageTitle,
  createTitle,
  loadingMessage,
  errorMessage,
  showAvatar = true,
  showStatus = true,
  statusBadgeType,
  avatarEditable = false,
  onAvatarChange,
  onAvatarRemove,
  isUploadingAvatar = false,
  getStatusText,
  actionButtons = [],
  forceActionButtonsDropdown = false,
  footerContent,
  hideFooter = false,
  useGenericFooter = false,
  footerProps = {},
  disabledTabs = [],
  onCancelEditChange,
  modals,
  errorActions = [],
  setTabValid,
  patchResultToDisplay = null,
}: FeatureDetailsProps<T, TFormData>) {
  const { t } = useTranslation(['common']);

  // Form validation state - controls save/create button enabled/disabled
  const [currentTabIsDirty, setCurrentTabIsDirty] = useState(false);
  // Pure changes state - controls confirmation dialogs (back button, tab switching)
  const [currentTabHasChanges, setCurrentTabHasChanges] = useState(false);
  // Form validity state - controls whether submit is allowed
  const [currentTabIsValid, setCurrentTabIsValid] = useState(true);

  // Ref to hold allowNavigation function from unsaved changes guard
  // This is used to bypass the guard after successful save operations
  const allowNavigationRef = useRef<(() => void) | null>(null);

  // Extract tab refs and content from unified tabs structure
  const tabRefs = React.useMemo(() => {
    return tabs.reduce(
      (acc, tab) => {
        acc[tab.id] = tab.ref;
        return acc;
      },
      {} as Record<string, React.RefObject<FeatureTabRef<TFormData> | null>>
    );
  }, [tabs]);

  const tabContent = React.useMemo(() => {
    return tabs.reduce(
      (acc, tab) => {
        acc[tab.id] = tab.content;
        return acc;
      },
      {} as Record<string, React.ReactNode>
    );
  }, [tabs]);

  // Transform tabs to include disabled state for EditChange mode
  const tabsWithDisabled = React.useMemo(() => {
    return tabs.map(tab => ({
      ...tab,
      disabled: disabledTabs.includes(tab.id),
    }));
  }, [tabs, disabledTabs]);

  // Wrap handleSave to include allowNavigation in the success callback
  // This ensures navigation is allowed after successful save (create/update)
  // Uses ref because allowNavigation isn't available yet at this point
  const handleSaveWithNavigationBypass = useCallback(
    (params: { data: TFormData; successCallback?: () => void }) => {
      handleSave({
        ...params,
        successCallback: (result: PatchEntityResult) => {
          setModalPatchResultToDisplay(result);
          // Allow navigation to proceed (bypasses unsaved changes guard)
          allowNavigationRef.current?.();
          // Call original success callback if provided
          params.successCallback?.();
        },
      });
    },
    [handleSave]
  );

  // Use tab form management hook for all form coordination
  const {
    hasAnyData: formHasValidData,
    saveCurrentTabData,
    handleFormSubmit,
  } = useFeatureDetailsFormManager<T, TFormData>({
    mode,
    currentTab,
    tabRefs: tabRefs as Record<string, React.RefObject<FeatureTabRef<TFormData>>>,
    entityData,
    currentTabHasChanges: currentTabIsDirty, // This should only be used for button state validation
    hasDraftData,
    getDraftData,
    saveDraftData,
    clearDraft,
    getFinalSubmissionData,
    handleSave: handleSaveWithNavigationBypass,
    calculateHasAnyData: calculateHasAnyData
      ? context =>
          calculateHasAnyData({
            ...context,
            tabs,
          })
      : undefined,
  });

  // Form state change handler - updates both internal state and notifies parent
  const handleTabFormStateChange = useCallback(
    (isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => {
      // isDirty is for validation/save button only
      setCurrentTabIsDirty(isDirty);
      onFormStateChange(isDirty);

      // hasChanges is for confirmations only
      if (hasChanges !== undefined) {
        setCurrentTabHasChanges(hasChanges);
      }

      // isValid controls whether submit is allowed
      if (isValid !== undefined) {
        setCurrentTabIsValid(isValid);
        // Only clear error icons in real-time (when form becomes valid).
        // Error icons are set on tab switch in executeTabChange.
        if (isValid) {
          setTabValid?.(currentTab, true);
        }
      }
    },
    [onFormStateChange, currentTab, setTabValid]
  );

  // Unsaved changes guard - intercepts navigation at router level
  const shouldUseUnsavedChangesGuard =
    enableUnsavedChangesConfirmation &&
    (mode === FeatureMode.Edit || mode === FeatureMode.Create || mode === FeatureMode.EditChange);

  // Determine if form is dirty for navigation protection
  // Create mode: check if current tab has user-modified data OR other tabs have draft data
  // Edit/EditChange mode: check if current tab has unsaved changes
  const isFormDirtyForNavigation =
    mode === FeatureMode.Create ? currentTabHasChanges || hasDraftData : currentTabHasChanges;

  const [modalPatchResultToDisplay, setModalPatchResultToDisplay] =
    useState<PatchEntityResult | null>(null);

  useEffect(() => {
    setModalPatchResultToDisplay(patchResultToDisplay);
  }, [patchResultToDisplay]);

  const {
    isModalOpen,
    handleModalDiscard,
    handleModalCancel,
    checkUnsavedChanges,
    allowNavigation,
  } = useUnsavedChangesGuard({
    isDirty: shouldUseUnsavedChangesGuard ? isFormDirtyForNavigation : false,
    onDiscard: () => {
      // Reset form state when discarding changes
      setCurrentTabIsDirty(false);
      setCurrentTabHasChanges(false);
    },
  });

  // Store allowNavigation in ref so it can be used by handleSaveWithNavigationBypass
  // This is needed because handleSaveWithNavigationBypass is defined before the guard hook
  useEffect(() => {
    allowNavigationRef.current = allowNavigation;
  }, [allowNavigation]);

  // Provide allowNavigation function to parent component if callback is provided
  useEffect(() => {
    if (onAllowNavigationReady) {
      onAllowNavigationReady(allowNavigation);
    }
  }, [onAllowNavigationReady, allowNavigation]);

  // Cancel handler for EditChange mode
  const handleCancelEditChange = useCallback(() => {
    if (onCancelEditChange) {
      onCancelEditChange();
    }
  }, [onCancelEditChange]);

  // Provide form state change handler to parent component for tab content creation
  useEffect(() => {
    if (onFormStateChangeHandlerReady) {
      onFormStateChangeHandlerReady(handleTabFormStateChange);
    }
  }, [onFormStateChangeHandlerReady, handleTabFormStateChange]);

  // When tab changes, check if new tab has a form ref
  // If not, immediately reset form state to clean
  useEffect(() => {
    const currentTabRef = tabRefs[currentTab];
    if (!currentTabRef?.current) {
      // Tab doesn't have a form, ensure state is clean
      setCurrentTabIsDirty(false);
      setCurrentTabHasChanges(false);
      setCurrentTabIsValid(true);
      onFormStateChange(false);
    }
    // Only run when currentTab changes, not when tabRefs or onFormStateChange changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Internal tab change logic (called after confirmation if needed)
  const executeTabChange = useCallback(
    async (newTab: string, additionalParams?: Record<string, string>) => {
      // Capture current tab's validation state before switching
      // In create mode, always check validity (tab may be invalid even if untouched)
      // In edit mode, only check if the form has been modified
      const currentTabRef = tabRefs[currentTab];
      const shouldCheckValidity =
        mode === FeatureMode.Create || currentTabIsDirty || currentTabHasChanges;
      if (currentTabRef?.current && setTabValid && shouldCheckValidity) {
        const isCurrentTabValid = currentTabRef.current.isValid;
        setTabValid(currentTab, isCurrentTabValid);
      }

      if (mode === FeatureMode.Create) {
        try {
          await saveCurrentTabData();
        } catch {
          // Draft save errors are non-critical
        }
      }

      // Reset dirty state - it will be set again by the new tab's form if it has changes
      setCurrentTabIsDirty(false);
      setCurrentTabHasChanges(false);
      setCurrentTabIsValid(true);

      // If switching to a tab without a valid form ref, ensure parent knows it's not dirty
      const newTabRef = tabRefs[newTab];
      if (!newTabRef?.current) {
        onFormStateChange(false);
      }

      parentOnTabChange(newTab, additionalParams);
    },
    [
      mode,
      saveCurrentTabData,
      parentOnTabChange,
      tabRefs,
      onFormStateChange,
      currentTab,
      setTabValid,
      currentTabIsDirty,
      currentTabHasChanges,
    ]
  );

  // Tab change with optional additional query params
  // Checks for unsaved changes before allowing tab switch
  const handleTabChange = useCallback(
    (newTab: string, additionalParams?: Record<string, string>) => {
      // Skip check if already on the same tab
      if (newTab === currentTab) {
        return;
      }

      // Check for unsaved changes only in Edit/EditChange modes
      // In Create mode, allow free tab switching (guard only protects navigation away from page)
      const shouldCheckUnsavedForTabSwitch =
        enableUnsavedChangesConfirmation &&
        (mode === FeatureMode.Edit || mode === FeatureMode.EditChange) &&
        currentTabHasChanges;

      if (shouldCheckUnsavedForTabSwitch) {
        // Store the tab change action to execute after user confirms
        const shouldProceed = checkUnsavedChanges(() => {
          // Force state reset before executing tab change
          setCurrentTabIsDirty(false);
          setCurrentTabHasChanges(false);
          setCurrentTabIsValid(true);

          // Execute tab change
          executeTabChange(newTab, additionalParams);
        });

        // If form is clean, proceed immediately
        if (shouldProceed) {
          executeTabChange(newTab, additionalParams);
        }
      } else {
        // No unsaved changes or guard disabled, proceed immediately
        executeTabChange(newTab, additionalParams);
      }
    },
    [
      currentTab,
      enableUnsavedChangesConfirmation,
      mode,
      currentTabHasChanges,
      checkUnsavedChanges,
      executeTabChange,
    ]
  );

  // Provide wrapped tab change handler to parent component for tab content
  // This ensures tabs can trigger tab changes with unsaved changes protection
  // Use ref to avoid infinite loop from handleTabChange recreation
  const handleTabChangeRef = useRef(handleTabChange);
  useEffect(() => {
    handleTabChangeRef.current = handleTabChange;
  }, [handleTabChange]);

  useEffect(() => {
    if (onTabChangeHandlerReady) {
      // Provide stable wrapper that uses the ref
      onTabChangeHandlerReady((tab: string, additionalParams?: Record<string, string>) => {
        handleTabChangeRef.current(tab, additionalParams);
      });
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    const defaultErrorActions = [
      ...(onRefetch
        ? [
            {
              label: t('common:error.reload'),
              onClick: onRefetch,
              variant: 'default' as const,
              icon: <RefreshCw className="h-4 w-4" />,
            },
          ]
        : []),
      ...errorActions,
    ];

    return (
      <ErrorState
        error={errorMessage || t('common:error.reloadPage')}
        actions={defaultErrorActions}
      />
    );
  }

  const displayTitle =
    mode === FeatureMode.Create ? createTitle || pageTitle : entityData?.name || pageTitle;

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      {/* Show content-area loader when loading entity details */}
      {isLoadingEntity ? (
        <FullPageLoader message={loadingMessage} fullViewport={false} />
      ) : (
        <>
          {/* Header */}
          <div className="z-[5] shrink-0 border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={mode === FeatureMode.EditChange ? handleCancelEditChange : onBack}
                  className="h-7 w-7 flex-shrink-0 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Entity Avatar - only show in edit/read/editChange modes and when enabled */}
                {showAvatar && mode !== FeatureMode.Create && (
                  <EntityAvatar
                    image={entityData?.logo || entityData?.info?.logoUrl || entityData?.avatarLink}
                    name={entityData?.name || entityData?.info?.name || ''}
                    editable={
                      avatarEditable &&
                      (mode === FeatureMode.Edit || mode === FeatureMode.EditChange)
                    }
                    onImageChange={onAvatarChange}
                    onImageRemove={onAvatarRemove}
                    isUploading={isUploadingAvatar}
                    size="md"
                    shape="rounded"
                    alt={`${entityData?.name || entityData?.info?.name || ''} logo`}
                    withBorder
                  />
                )}

                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <h1 className="truncate text-xl font-semibold leading-none text-foreground sm:text-2xl">
                      {displayTitle}
                    </h1>
                    {showStatus && (entityData?.status || entityData?.info?.status) && (
                      <div className="flex flex-shrink-0 items-center">
                        <StatusBadge
                          type={statusBadgeType}
                          status={(entityData.status || entityData.info?.status) as string}
                          label={
                            getStatusText?.(
                              (entityData.status || entityData.info?.status) as any
                            ) ||
                            entityData.status ||
                            (entityData.info?.status as string)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-2">
                {entityData && actionButtons.length > 0 && (
                  <ActionButtons
                    buttons={actionButtons}
                    forceDropdown={forceActionButtonsDropdown}
                  />
                )}
              </div>
            </div>

            {/* Mobile tabs row - aligned with back button */}
            <div className="mt-3 flex items-center gap-4 md:hidden">
              <MobileTabs
                activeTab={currentTab}
                onTabChange={handleTabChange}
                tabs={tabsWithDisabled}
              />
            </div>
          </div>

          {/* Content area - flex-1 to fill between header and footer */}
          <div className="min-h-0 flex-1 pl-4 pr-0.5">
            <ResponsiveTabs
              activeTab={currentTab}
              onTabChange={handleTabChange}
              tabs={tabsWithDisabled}
            >
              {Object.entries(tabContent).map(([tabId, content]) => {
                const tab = tabs.find(t => t.id === tabId);
                // A tab that owns its own scroll (fillAvailableHeight) manages
                // its own width so its scrollbar can sit flush at the right
                // edge; the max-width cap would otherwise inset the scrollbar.
                const maxWidth = tab?.fillAvailableHeight ? '' : tab?.maxWidth || 'max-w-4xl';
                return (
                  currentTab === tabId && (
                    <div
                      key={tabId}
                      className={`${maxWidth} ${tab?.fillAvailableHeight ? 'h-full min-h-0' : ''}`}
                    >
                      {content}
                    </div>
                  )
                );
              })}
            </ResponsiveTabs>
          </div>

          {/* Footer */}
          {footerContent ||
            (useGenericFooter && (
              <FeatureDetailsFooter
                mode={mode}
                isLoading={isLoading}
                hasFormChanges={mode === FeatureMode.Create ? formHasValidData : currentTabIsDirty}
                isFormValid={currentTabIsValid}
                onSubmit={handleFormSubmit}
                onCancel={mode === FeatureMode.EditChange ? handleCancelEditChange : undefined}
                selectedDate={
                  mode === FeatureMode.EditChange ? footerProps.selectedDate : undefined
                }
                t={t}
                isVisible={!hideFooter}
                {...footerProps}
              />
            ))}
        </>
      )}

      {/* Modals and overlays */}
      {modals}

      {/* Built-in unsaved changes guard modal */}
      {shouldUseUnsavedChangesGuard && (
        <UnsavedChangesModal
          open={isModalOpen}
          onClose={handleModalCancel}
          onDiscard={handleModalDiscard}
        />
      )}

      <PatchEntityDetailsResultModal
        resultToDisplay={modalPatchResultToDisplay}
        onClose={() => {
          setModalPatchResultToDisplay(null);
        }}
      />
    </div>
  );
}

/**
 * Action buttons component for feature operations.
 * Renders desktop buttons and mobile dropdown menu with proper responsive behavior.
 */
interface ActionButtonsProps {
  buttons: ActionButtonConfig[];
  forceDropdown?: boolean;
}

function ActionButtons({ buttons, forceDropdown = false }: ActionButtonsProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleAction = (onClick: () => void) => {
    onClick();
    setIsDropdownOpen(false);
  };

  // Use larger breakpoint when there are more than 2 buttons; force dropdown at all sizes when requested.
  const desktopClass = forceDropdown
    ? 'hidden'
    : buttons.length > 2
      ? 'hidden items-center gap-2 lg:flex'
      : 'hidden items-center gap-2 md:flex';
  const mobileClass = forceDropdown ? 'flex' : buttons.length > 2 ? 'lg:hidden' : 'md:hidden';

  return (
    <>
      {/* Desktop buttons - adjust breakpoint based on number of buttons */}
      <div className={desktopClass}>
        {buttons.map((button, index) => (
          <Button
            key={index}
            variant={button.variant || 'outline'}
            size="sm"
            onClick={button.onClick}
            className={button.className}
          >
            <button.icon className="mr-2 h-4 w-4" />
            {button.label}
          </Button>
        ))}
      </div>

      {/* Mobile dropdown - adjust breakpoint based on number of buttons */}
      <div className={mobileClass}>
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 w-9 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {buttons.map((button, index) => (
              <div key={index}>
                <DropdownMenuItem
                  onClick={() => handleAction(button.onClick)}
                  className={
                    button.variant === 'destructive' || button.variant === 'outlineDestructive'
                      ? 'text-destructive'
                      : undefined
                  }
                >
                  <button.icon className="mr-2 h-4 w-4" />
                  {button.label}
                </DropdownMenuItem>
                {index < buttons.length - 1 && <DropdownMenuSeparator />}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
